// assets/product-customization.js
// Native integration with the Prestige theme.

class ProductCustomization {
  constructor(container) {
    this.container = container;
    this.customizationType = container.dataset.customMode;
    this.productId = container.dataset.productId;
    this.formId = container.dataset.formId;

    this.init();
  }

  init() {
    this.attachCharCounters();

    switch (this.customizationType) {
      case 'Variant-Based Multi-Input':
      case 'Variant-Based Multi-Input and Birthstone':
      case "Dad's Name and Mom's Name and Variant-Based Multi-Input":
        this.initVariantMultiInput();
        break;
      case 'Conditional Toggle (Yes/No)':
      case 'Conditional Toggle (Yes/No) Double input':
        this.initConditionalToggle();
        break;
    }
    this.initCustomSelects();
  }

  // ─── Character counters ──────────────────────────────────────────────────

  attachCharCounters() {
    this.container.querySelectorAll('[data-char-counter]').forEach(input => {
      const wrapper = input.closest('.input-wrapper');
      const counter = wrapper && wrapper.querySelector('.char-counter');
      if (!counter) return;

      const max = input.maxLength > 0 ? input.maxLength : 10;
      counter.textContent = `${input.value.length}/${max}`;

      input.addEventListener('input', () => {
        counter.textContent = `${input.value.length}/${max}`;
      });
    });
  }

  // ─── Option 3: Variant-Based Multi-Input ────────────────────────────────

  initVariantMultiInput() {
    this.multiContainer = this.container.querySelector(
      `#variant-multiplier-container-${this.productId}`
    );

    // Render inputs for the currently selected variant on page load
    this.renderFromCurrentVariant();

    // PRIMARY: Listen to radio changes synchronously (no async dependency).
    // The option radio inputs fire 'change' when the user clicks them.
    // Each label has data-multiline-input="{{ label | split: ' ' | first }}"
    // so "2 Beads" → data-multiline-input="2", "Gold" → data-multiline-input="Gold".
    // We scan all currently checked options and use the first numeric one as count.
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (
        target.tagName !== 'INPUT' ||
        target.type !== 'radio' ||
        !target.dataset.optionPosition ||
        target.getAttribute('form') !== this.formId
      ) return;

      this.updateFromCheckedRadios();
    });

    document.addEventListener('variant:change', (event) => {
      const { formId } = event.detail || {};
      if (formId !== this.formId) return;
      setTimeout(() => this.updateFromCheckedRadios(), 10);
    });
  }

  updateFromCheckedRadios() {
    let count = 0;

    const checkedInputs = Array.from(
      document.querySelectorAll(
        `input[data-option-position][form="${CSS.escape(this.formId)}"]:checked`
      )
    );

    for (const input of checkedInputs) {
      const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (label && label.dataset.multilineInput) {
        const num = parseInt(label.dataset.multilineInput, 10);
        if (!isNaN(num) && num > 0) {
          count = num;
          break;
        }
      }
    }

    if (count > 0) {
      this.renderMultiInputs(count);
    } else {
      this.renderMultiInputs(0);
    }
  }

  renderFromCurrentVariant() {
    this.updateFromCheckedRadios();
  }

  renderMultiInputs(count) {
    if (!this.multiContainer) return;

    if (count <= 0) {
      this.showPlaceholder();
      return;
    }

    // Read Group 1 metafield values passed from Liquid via data-field-* attributes.
    // All inputs in variant-based mode share Group 1 (variant-based override rule).
    const label       = this.multiContainer.dataset.fieldLabel       || 'Name';
    const placeholder = this.multiContainer.dataset.fieldPlaceholder || 'Maximum 10 letters';
    const limit       = parseInt(this.multiContainer.dataset.fieldLimit, 10) || 10;

    let html = '';
    for (let i = 1; i <= count; i++) {
      html += `
        <div class="custom-input-group" data-index="${i}">
          <label for="name-${i}-${this.productId}">
            ${label} ${i} <span class="required">*</span>
          </label>
          <div class="input-wrapper">
            <input
              type="text"
              id="name-${i}-${this.productId}"
              name="properties[${label} ${i}]"
              class="custom-input"
              placeholder="${placeholder}"
              maxlength="${limit}"
              required
              autocomplete="off"
              form="${this.formId}"
              data-char-counter
            >
            <small class="char-counter">0/${limit}</small>
          </div>
        </div>
      `;
    }

    this.multiContainer.innerHTML = html;
    this.attachCharCounters(); // wire up counters for newly created inputs
  }

  showPlaceholder() {
    if (this.multiContainer) {
      this.multiContainer.innerHTML =
        '<div class="loading-placeholder">Select a variant to see customization options</div>';
    }
  }

  // ─── Option 4: Conditional Toggle (Yes/No) ──────────────────────────────
  //
  // Visiblity is controlled by variant options. When a user selects a variant
  // option that maps to "Yes" (via data-multiline-input="Yes" on its label),
  // the text input container is shown and enabled.
  //
  // The text input (properties[Special Name]) uses the `disabled` attribute
  // when hidden. Disabled fields are excluded from form submission entirely,
  // so no blank property reaches the cart.

  initConditionalToggle() {
    const textInputs = this.container.querySelectorAll('input[type="text"]');

    const YES_TOKENS = ['yes', 'sí', 'si', 'oui', 'ja', 'sim', 'sì', 'da', 'tak'];

    const updateToggleFromRadios = () => {
      const checkedInputs = Array.from(
        document.querySelectorAll(
          `input[data-option-position][form="${CSS.escape(this.formId)}"]:checked`
        )
      );

      let isYes = false;

      // Strategy 1: read data-multiline-input on labels.
      // Strips leading punctuation (e.g. ¡) before matching.
      for (const input of checkedInputs) {
        const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!label) continue;
        const raw = (label.dataset.multilineInput || '').toLowerCase().replace(/^[¡¿!?.\s]+/, '').trim();
        if (YES_TOKENS.some(t => raw.startsWith(t))) {
          isYes = true;
          break;
        }
      }

      // Strategy 2: check variant title as a fallback
      if (!isYes && checkedInputs.length > 0) {
        const optionTexts = checkedInputs.map(input => {
          const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          return label ? label.textContent.trim() : '';
        });
        const variantTitle = optionTexts.join(' / ');
        const variantData = (window.variantData || {})[this.productId] || [];
        const matched = variantData.find(v => v.title === variantTitle);
        if (matched && matched.title.toLowerCase().includes('yes')) {
          isYes = true;
        }
      }

      this.container.hidden = !isYes;

      textInputs.forEach(input => {
        input.disabled = !isYes;
        input.required = isYes;
        if (!isYes) input.value = '';
      });
    };

    // Initialize state on load
    updateToggleFromRadios();

    // Listen to radio changes synchronously
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (
        target.tagName !== 'INPUT' ||
        target.type !== 'radio' ||
        !target.dataset.optionPosition ||
        target.getAttribute('form') !== this.formId
      ) return;

      updateToggleFromRadios();
    });

    // Also listen to prestige's variant:change
    document.addEventListener('variant:change', (event) => {
      const { formId } = event.detail || {};
      if (formId !== this.formId) return;
      updateToggleFromRadios();
    });
  }

  // ─── Custom Selects (Birthstones with images) ──────────────────────────
  initCustomSelects() {
    this.container.querySelectorAll('[data-custom-select]').forEach(wrapper => {
      const select = wrapper.querySelector('select');
      if (!select) return;

      select.style.display = 'none';

      const trigger = document.createElement('div');
      trigger.className = 'custom-select-trigger';
      trigger.tabIndex = 0;

      const selectedValueContainer = document.createElement('div');
      selectedValueContainer.className = 'custom-select-value';
      
      const initialOption = select.options[select.selectedIndex];
      if (initialOption) {
        if (initialOption.dataset.image) {
          const img = document.createElement('img');
          img.src = initialOption.dataset.image;
          selectedValueContainer.appendChild(img);
        }
        const selSpan = document.createElement('span');
        selSpan.textContent = initialOption.textContent;
        selectedValueContainer.appendChild(selSpan);
      } else {
        selectedValueContainer.innerHTML = '<span>Select...</span>';
      }

      const arrow = document.createElement('div');
      arrow.className = 'custom-select-arrow';

      trigger.appendChild(selectedValueContainer);
      trigger.appendChild(arrow);

      const dropdown = document.createElement('div');
      dropdown.className = 'custom-select-dropdown';

      Array.from(select.options).forEach(option => {
        if (option.disabled && !option.value) return;

        const item = document.createElement('div');
        item.className = 'custom-select-option';
        item.dataset.value = option.value;

        if (option.dataset.image) {
          const img = document.createElement('img');
          img.src = option.dataset.image;
          item.appendChild(img);
        }

        const span = document.createElement('span');
        span.textContent = option.textContent;
        item.appendChild(span);

        item.addEventListener('click', () => {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));

          selectedValueContainer.innerHTML = '';
          if (option.dataset.image) {
            const img = document.createElement('img');
            img.src = option.dataset.image;
            selectedValueContainer.appendChild(img);
          }
          const selSpan = document.createElement('span');
          selSpan.textContent = option.textContent;
          selectedValueContainer.appendChild(selSpan);

          dropdown.classList.remove('open');
          trigger.classList.remove('open');
        });

        dropdown.appendChild(item);
      });

      wrapper.appendChild(trigger);
      wrapper.appendChild(dropdown);

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = dropdown.classList.contains('open');
        document.querySelectorAll('.custom-select-dropdown.open').forEach(d => d.classList.remove('open'));
        document.querySelectorAll('.custom-select-trigger.open').forEach(t => t.classList.remove('open'));

        if (!isOpen) {
          dropdown.classList.add('open');
          trigger.classList.add('open');
        }
      });

      document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target)) {
          dropdown.classList.remove('open');
          trigger.classList.remove('open');
        }
      });
    });
  }
}

// ─── Bootstrap ──────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-customizer-container]').forEach(wrapper => {
    new ProductCustomization(wrapper);
  });
});
